import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, Trash2, Plus, LogOut, ArrowLeft, Calendar, UserX } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { adminLogin, adminBookings, adminCreateBooking, adminCancelBooking, adminGoogleStatus, adminGoogleConnect, adminGoogleDisconnect, adminNoShow } from "../lib/api";
import { Proposals } from "../components/admin/Proposals";
import { Invoices } from "../components/admin/Invoices";
import { Clients } from "../components/admin/Clients";
import { Availability } from "../components/admin/Availability";

const SERVICES = [
    { id: "web-dev", name: "Website & Web App Development" },
    { id: "crm-dev", name: "Custom CRM & Business Systems" },
    { id: "social-content", name: "Social Media Content with AI" },
    { id: "workflow-automation", name: "Workflow Automation" },
    { id: "email-automation", name: "Email Automation" },
    { id: "web-building", name: "Build-Your-Own-Website Class" },
    { id: "custom", name: "Custom Topic" },
];

const TABS = [
    { id: "bookings", label: "Bookings" },
    { id: "availability", label: "Availability" },
    { id: "clients", label: "Clients" },
    { id: "proposals", label: "Proposals" },
    { id: "invoices", label: "Invoices" },
];

const fmtTime = (t) => {
    const [h, m] = t.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const hr = h % 12 === 0 ? 12 : h % 12;
    return `${hr}:${String(m).padStart(2, "0")} ${ampm}`;
};

export default function Admin() {
    const [token, setToken] = useState(sessionStorage.getItem("cw_admin_token") || "");
    const [password, setPassword] = useState("");
    const [loggingIn, setLoggingIn] = useState(false);
    const [bookings, setBookings] = useState([]);
    const [mForm, setMForm] = useState({ name: "", email: "", phone: "", service: "custom", service_label: "", date: "", time: "", duration_hours: 1, notes: "", location: "virtual" });
    const [tab, setTab] = useState("bookings");
    const [saving, setSaving] = useState(false);
    const [gcal, setGcal] = useState(null);
    const [searchParams] = useSearchParams();

    useEffect(() => {
        if (searchParams.get("gcal") === "connected") toast.success("Google Calendar connected.");
        if (searchParams.get("gcal") === "error") toast.error("Google connection failed — try again.");
    }, [searchParams]);

    const connectGoogle = async () => {
        try {
            const r = await adminGoogleConnect(token);
            window.location.href = r.authorization_url;
        } catch (err) {
            toast.error(err.response?.data?.detail || "Google connection is not configured yet.");
        }
    };

    const disconnectGoogle = async () => {
        try {
            await adminGoogleDisconnect(token);
            setGcal({ connected: false });
            toast.success("Google Calendar disconnected.");
        } catch (e) {
            toast.error("Could not disconnect.");
        }
    };

    const load = async (t) => {
        try {
            const [b, g] = await Promise.all([adminBookings(t), adminGoogleStatus(t)]);
            setBookings(b);
            setGcal(g);
        } catch (e) {
            if (e.response?.status === 401) {
                sessionStorage.removeItem("cw_admin_token");
                setToken("");
                toast.error("Session expired — please log in again.");
            } else {
                toast.error("Could not load bookings.");
            }
        }
    };

    useEffect(() => {
        if (token) load(token);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const login = async (e) => {
        e.preventDefault();
        setLoggingIn(true);
        try {
            const r = await adminLogin(password);
            sessionStorage.setItem("cw_admin_token", r.token);
            setToken(r.token);
            toast.success("Welcome back, Alex.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Login failed.");
        } finally {
            setLoggingIn(false);
        }
    };

    const logout = () => {
        sessionStorage.removeItem("cw_admin_token");
        setToken("");
    };

    const cancelBooking = async (id) => {
        try {
            await adminCancelBooking(token, id);
            toast.success("Booking cancelled — the slot is open again.");
            load(token);
        } catch (e) {
            toast.error("Could not cancel booking.");
        }
    };

    const markNoShow = async (b) => {
        try {
            await adminNoShow(token, b.booking_id);
            toast.success(
                b.email
                    ? "Marked no-show — a $25 fee payment link was emailed to the client."
                    : "Marked no-show — no email on file, so send the fee link manually."
            );
            load(token);
        } catch (e) {
            toast.error("Could not mark no-show.");
        }
    };

    const addBooking = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await adminCreateBooking(token, mForm);
            toast.success(mForm.email ? "Booking added — confirmation email sent." : "Booking added.");
            setMForm({ name: "", email: "", phone: "", service: "custom", service_label: "", date: "", time: "", duration_hours: 1, notes: "", location: "virtual" });
            load(token);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not add booking.");
        } finally {
            setSaving(false);
        }
    };

    if (!token) {
        return (
            <div className="bg-paper text-ink min-h-screen flex items-center justify-center px-5" data-testid="admin-login-page">
                <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-sm border border-ink/10 bg-white p-8">
                    <Lock size={28} className="text-worx" />
                    <h1 className="mt-4 font-display font-semibold text-2xl tracking-tight">Owner console</h1>
                    <p className="mt-1 text-sm text-ink/50">Enter your admin password to manage bookings.</p>
                    <Label htmlFor="admin-pw" className="mt-6 block">Password</Label>
                    <Input
                        id="admin-pw"
                        data-testid="admin-password-input"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="mt-1.5 rounded-none"
                    />
                    <Button data-testid="admin-login-submit-btn" type="submit" disabled={loggingIn} className="mt-5 w-full bg-ink hover:bg-worx text-paper rounded-none py-5">
                        {loggingIn ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                        Log in
                    </Button>
                    <Link to="/" className="mt-4 inline-flex items-center gap-1.5 text-xs text-ink/40 hover:text-worx transition-colors">
                        <ArrowLeft size={12} /> Back to site
                    </Link>
                </form>
            </div>
        );
    }

    return (
        <div className="bg-paper text-ink min-h-screen" data-testid="admin-dashboard">
            <header className="border-b border-ink/10 bg-white">
                <div className="mx-auto max-w-6xl px-5 md:px-8 h-16 flex items-center justify-between">
                    <span className="font-display font-semibold tracking-tight text-lg">Consulting WorX — Owner console</span>
                    <div className="flex items-center gap-3">
                        <Link to="/" className="text-sm text-ink/60 hover:text-worx transition-colors">View site</Link>
                        <Link to="/brand" data-testid="admin-brand-link" className="text-sm text-ink/60 hover:text-worx transition-colors">Brand assets</Link>
                        <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-1.5 text-sm border border-ink/15 px-3 py-1.5 hover:border-ink transition-colors">
                            <LogOut size={14} /> Log out
                        </button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-6xl px-5 md:px-8 py-10">
                <div className="flex flex-wrap gap-1 mb-8 border-b border-ink/10" data-testid="admin-tabs">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            data-testid={`admin-tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={`px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors duration-200 ${
                                tab === t.id
                                    ? "border-worx text-ink font-medium"
                                    : "border-transparent text-ink/50 hover:text-ink"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {tab === "bookings" && (
                <div className="grid lg:grid-cols-5 gap-8">
                <section className="lg:col-span-3 border border-ink/10 bg-white" data-testid="admin-bookings-panel">
                    <div className="px-6 py-4 border-b border-ink/10 flex items-center justify-between">
                        <h2 className="font-display font-medium text-xl">Upcoming bookings</h2>
                        <span className="text-sm text-ink/40">{bookings.length} total</span>
                    </div>
                    {bookings.length === 0 ? (
                        <p data-testid="admin-bookings-empty" className="px-6 py-10 text-ink/50 text-sm">No upcoming bookings.</p>
                    ) : (
                        <ul data-testid="admin-bookings-list" className="divide-y divide-ink/10">
                            {bookings.map((b) => (
                                <li key={b.booking_id} className="px-6 py-4 flex items-start justify-between gap-4" data-testid={`admin-booking-${b.booking_id.slice(0, 8)}`}>
                                    <div>
                                        <p className="font-medium">
                                            {b.date} · {fmtTime(b.time)} — {b.name}
                                        </p>
                                        <p className="text-sm text-ink/50 mt-0.5">
                                            {b.service_name} · {b.email || "no email"} {b.phone ? `· ${b.phone}` : ""}
                                            {b.notes ? ` · “${b.notes}”` : ""}
                                        </p>
                                        {b.status === "no-show" ? (
                                            <span className="mt-1.5 inline-block text-xs px-2 py-0.5 bg-worx text-white" data-testid={`booking-noshow-badge-${b.booking_id.slice(0, 8)}`}>
                                                No-show{b.fee_paid ? " · fee paid" : ""}
                                            </span>
                                        ) : b.price === 0 ? (
                                            <span className="mt-1.5 inline-block text-xs px-2 py-0.5 bg-ink/5 text-ink/50">Free call</span>
                                        ) : (
                                            <span className={`mt-1.5 inline-block text-xs px-2 py-0.5 ${b.payment_status === "paid" ? "bg-worx/10 text-worx" : "bg-ink/5 text-ink/50"}`}>
                                                {b.payment_status === "paid" ? "Paid" : "Unpaid"}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        {b.status !== "no-show" && (
                                            <button
                                                data-testid={`booking-noshow-${b.booking_id.slice(0, 8)}`}
                                                onClick={() => markNoShow(b)}
                                                className="text-ink/40 hover:text-worx transition-colors p-1"
                                                title="Mark no-show — emails client a $25 fee link"
                                            >
                                                <UserX size={16} />
                                            </button>
                                        )}
                                        <button
                                            data-testid={`booking-cancel-${b.booking_id.slice(0, 8)}`}
                                            onClick={() => cancelBooking(b.booking_id)}
                                            className="text-ink/40 hover:text-worx transition-colors p-1"
                                            title="Cancel booking"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <div className="lg:col-span-2 space-y-8">
                    <section className="border border-ink/10 bg-white" data-testid="admin-gcal-panel">
                        <div className="px-6 py-4 border-b border-ink/10">
                            <h2 className="font-display font-medium text-xl flex items-center gap-2">
                                <Calendar size={18} className="text-worx" /> Google Calendar
                            </h2>
                        </div>
                        <div className="p-6">
                            {gcal?.connected ? (
                                <div className="flex items-center justify-between gap-3">
                                    <p className="text-sm text-ink/70" data-testid="gcal-connected-text">
                                        Connected{gcal.email ? <> as <strong>{gcal.email}</strong></> : ""} — new bookings appear on your calendar automatically.
                                    </p>
                                    <button data-testid="gcal-disconnect-btn" onClick={disconnectGoogle} className="text-sm border border-ink/15 px-3 py-1.5 hover:border-worx hover:text-worx transition-colors shrink-0">
                                        Disconnect
                                    </button>
                                </div>
                            ) : (
                                <div>
                                    <p className="text-sm text-ink/60 leading-relaxed">
                                        Connect your Google account so every booking lands on your calendar automatically.
                                    </p>
                                    <button data-testid="gcal-connect-btn" onClick={connectGoogle} className="mt-4 bg-ink text-paper px-5 py-2.5 text-sm hover:bg-worx transition-colors">
                                        Connect Google Calendar
                                    </button>
                                    {gcal && !gcal.credentials_configured && (
                                        <p className="mt-3 text-xs text-ink/40" data-testid="gcal-credentials-hint">
                                            Google Cloud credentials aren't set up yet — ask your developer to add them first.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </section>

                    <section className="border border-ink/10 bg-white" data-testid="admin-manual-booking-panel">
                        <div className="px-6 py-4 border-b border-ink/10">
                            <h2 className="font-display font-medium text-xl">Add a booking manually</h2>
                            <p className="text-xs text-ink/50 mt-1">For phone/text requests and custom times. Any date and time works — even outside your public schedule.</p>
                        </div>
                        <form onSubmit={addBooking} data-testid="manual-booking-form" className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label htmlFor="m-name">Name *</Label>
                                    <Input id="m-name" data-testid="manual-name-input" value={mForm.name} onChange={(e) => setMForm({ ...mForm, name: e.target.value })} required className="mt-1.5 rounded-none" />
                                </div>
                                <div>
                                    <Label htmlFor="m-phone">Phone</Label>
                                    <Input id="m-phone" data-testid="manual-phone-input" value={mForm.phone} onChange={(e) => setMForm({ ...mForm, phone: e.target.value })} className="mt-1.5 rounded-none" />
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="m-email">Email (sends confirmation if set)</Label>
                                <Input id="m-email" data-testid="manual-email-input" type="email" value={mForm.email} onChange={(e) => setMForm({ ...mForm, email: e.target.value })} className="mt-1.5 rounded-none" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label htmlFor="m-date">Date *</Label>
                                    <Input id="m-date" data-testid="manual-date-input" type="date" value={mForm.date} onChange={(e) => setMForm({ ...mForm, date: e.target.value })} required className="mt-1.5 rounded-none" />
                                </div>
                                <div>
                                    <Label htmlFor="m-time">Time *</Label>
                                    <Input id="m-time" data-testid="manual-time-input" type="time" value={mForm.time} onChange={(e) => setMForm({ ...mForm, time: e.target.value })} required className="mt-1.5 rounded-none" />
                                </div>
                            </div>
                            <div>
                                <Label>Topic</Label>
                                <Select value={mForm.service} onValueChange={(v) => setMForm({ ...mForm, service: v })}>
                                    <SelectTrigger data-testid="manual-service-select" className="mt-1.5 rounded-none">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {SERVICES.map((s) => (
                                            <SelectItem key={s.id} value={s.id} data-testid={`manual-service-${s.id}`}>{s.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            {mForm.service === "custom" && (
                                <div>
                                    <Label htmlFor="m-label">Custom title</Label>
                                    <Input id="m-label" data-testid="manual-label-input" value={mForm.service_label} onChange={(e) => setMForm({ ...mForm, service_label: e.target.value })} placeholder="e.g. Dev project kickoff" className="mt-1.5 rounded-none" />
                                </div>
                            )}
                            <div>
                                <Label>Duration</Label>
                                <Select value={String(mForm.duration_hours)} onValueChange={(v) => setMForm({ ...mForm, duration_hours: parseInt(v, 10) })}>
                                    <SelectTrigger data-testid="manual-duration-select" className="mt-1.5 rounded-none">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {[1, 2, 3, 4, 6, 8].map((h) => (
                                            <SelectItem key={h} value={String(h)} data-testid={`manual-duration-${h}`}>{h} hour{h > 1 ? "s" : ""}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Location</Label>
                                <Select value={mForm.location} onValueChange={(v) => setMForm({ ...mForm, location: v })}>
                                    <SelectTrigger data-testid="manual-location-select" className="mt-1.5 rounded-none">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="virtual" data-testid="manual-location-virtual">Virtual — Google Meet</SelectItem>
                                        <SelectItem value="in-person" data-testid="manual-location-in-person">In person</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label htmlFor="m-notes">Notes</Label>
                                <Textarea id="m-notes" data-testid="manual-notes-input" value={mForm.notes} onChange={(e) => setMForm({ ...mForm, notes: e.target.value })} rows={2} className="mt-1.5 rounded-none" />
                            </div>
                            <Button data-testid="manual-submit-btn" type="submit" disabled={saving} className="w-full bg-worx hover:bg-ink text-white rounded-none py-5">
                                {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Plus className="mr-2" size={16} />}
                                Add booking
                            </Button>
                        </form>
                    </section>
                </div>
                </div>
                )}

                {tab === "availability" && <Availability token={token} />}
                {tab === "proposals" && <Proposals token={token} />}
                {tab === "invoices" && <Invoices token={token} />}
                {tab === "clients" && <Clients token={token} />}
            </main>
        </div>
    );
}
